import { ReactNode, useState } from "react";
import { Tabs, Select, DatePicker, Table, Segmented, Button } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useProjects } from "../../queries/projects";
import { useUsers } from "../../queries/users";
import {
  useReportByProject, useReportByUser,
  useReportTimeline, useReportVelocity,
} from "../../queries/reports";
import { ProjectProps, SessionUser } from "../../types/interfaces";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { EmptyState } from "../../components/ui/States";
import { ExportButtons } from "../../components/pages/reports/ExportButtons";
import styles from "./reports.module.css";

const { RangePicker } = DatePicker;

const defaultRange = (): [Dayjs, Dayjs] => [dayjs().startOf("month"), dayjs().endOf("day")];
const hrs = (m: number) => (m / 60).toFixed(1);

const Filters = ({ children, onClear }: { children: ReactNode; onClear: () => void }) => (
  <div className={styles.filters}>
    <span className={styles.filterLabel}>Scope</span>
    {children}
    <Button onClick={onClear}>Clear filters</Button>
  </div>
);

const Stats = ({ items }: { items: { label: string; value: string; note?: string }[] }) => (
  <div className={styles.stats}>
    {items.map((s) => (
      <div className={styles.stat} key={s.label}>
        <div className="osi-kicker">{s.label}</div>
        <div className={styles.statValue}>{s.value}</div>
        {s.note && <div className={styles.statNote}>{s.note}</div>}
      </div>
    ))}
  </div>
);

const pickNothing = (what: string) => (
  <EmptyState title={`Pick a ${what}`} description={`Choose a ${what} and a date range above to run the report.`} />
);

const DownloadTab = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, "day"), dayjs()]);
  const from = range[0].format("YYYY-MM-DD");
  const to = range[1].format("YYYY-MM-DD");
  const presets = [7, 30, 90].map((d) => ({ label: `Last ${d} days`, value: [dayjs().subtract(d - 1, "day"), dayjs()] as [Dayjs, Dayjs] }));

  return (
    <>
      <div className={styles.filters}>
        <span className={styles.filterLabel}>Period</span>
        <RangePicker value={range} presets={presets} allowClear={false} onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])} />
      </div>
      <div style={{ padding: 24, background: "var(--osi-surface)", border: "1px solid var(--osi-border)", borderRadius: "var(--osi-radius-lg)" }}>
        <div className="osi-kicker">Support team report</div>
        <p style={{ margin: "8px 0 16px", maxWidth: "var(--osi-reading-width)" }}>
          The manager dashboard as a file you can share: ticket response times against SLA, by severity, project and
          assignee; at-risk tickets; who is on which project and their open work; tasks by project; and weekly activity.
          No cost or rate data is included.
        </p>
        <ExportButtons from={from} to={to} />
      </div>
    </>
  );
};

const ByProjectTab = () => {
  const { data: projects } = useProjects();
  const [projectId, setProjectId] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: report, isLoading, isError, error } = useReportByProject(
    projectId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")
  );

  return (
    <>
      <Filters onClear={() => { setProjectId(undefined); setRange(defaultRange()); }}>
        <Select
          style={{ width: 240 }} placeholder="Project"
          value={projectId}
          options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          onChange={setProjectId}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!projectId ? pickNothing("project") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          {report && (
            <>
              <Stats items={[
                { label: "Total hours", value: hrs(report.totalMinutes) },
                { label: "Billable hours", value: hrs(report.billableMinutes), note: `${Math.round((report.billableMinutes / Math.max(report.totalMinutes, 1)) * 100)}% of logged time` },
              ]} />
              <Table
                rowKey="userId" size="small" pagination={false} dataSource={report.byUser}
                columns={[
                  { title: "User", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { title: "Hours", dataIndex: "totalMinutes", width: 120, align: "right", render: (v) => <span className="osi-num">{hrs(v)}</span> },
                ]}
              />
            </>
          )}
        </DisplayComponent>
      )}
    </>
  );
};

const ByUserTab = () => {
  const { data: users } = useUsers();
  const [userId, setUserId] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: report, isLoading, isError, error } = useReportByUser(
    userId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")
  );

  return (
    <>
      <Filters onClear={() => { setUserId(undefined); setRange(defaultRange()); }}>
        <Select
          style={{ width: 240 }} placeholder="User"
          value={userId}
          options={(users || []).map((u: SessionUser) => ({ label: u.name, value: u._id }))}
          onChange={setUserId}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {!userId ? pickNothing("user") : (
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          {report && (
            <>
              <Stats items={[
                { label: "Total hours", value: hrs(report.totalMinutes) },
              ]} />
              <Table
                rowKey="projectId" size="small" pagination={false}
                dataSource={report.byProject}
                columns={[
                  { title: "Project", dataIndex: "projectName", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
                  { title: "Hours", dataIndex: "totalMinutes", width: 120, align: "right", render: (v) => <span className="osi-num">{hrs(v)}</span> },
                ]}
              />
            </>
          )}
        </DisplayComponent>
      )}
    </>
  );
};

const TimelineTab = () => {
  const { data: projects } = useProjects();
  const [scope, setScope] = useState<"project" | "all">("all");
  const [scopeId, setScopeId] = useState<string>();
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  const { data: timeline, isLoading, isError, error } = useReportTimeline(
    scope, scopeId, range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD"), granularity
  );

  const peak = Math.max(...(timeline || []).map((t: any) => t.totalMinutes), 1);

  return (
    <>
      <Filters onClear={() => {
        setScope("all");
        setScopeId(undefined);
        setGranularity("day");
        setRange(defaultRange());
      }}>
        <Segmented
          value={scope}
          onChange={(v) => { setScope(v as "project" | "all"); setScopeId(undefined); }}
          options={[{ label: "All projects", value: "all" }, { label: "One project", value: "project" }]}
        />
        {scope === "project" && (
          <Select
            style={{ width: 240 }}
            placeholder="Project"
            options={(projects || []).map((item: ProjectProps) => ({ label: item.name, value: item._id }))}
            value={scopeId} onChange={setScopeId}
          />
        )}
        <Segmented
          value={granularity}
          onChange={(v) => setGranularity(v as "day" | "week" | "month")}
          options={[{ label: "Day", value: "day" }, { label: "Week", value: "week" }, { label: "Month", value: "month" }]}
        />
        <RangePicker value={range} onChange={(v) => v && setRange(v as [Dayjs, Dayjs])} />
      </Filters>
      {scope === "project" && !scopeId ? pickNothing("project") : (
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table
          rowKey="bucket" size="small" dataSource={timeline || []}
          pagination={false}
          locale={{ emptyText: <EmptyState title="No time logged in this range" description="Widen the date range, or pick a different project." /> }}
          columns={[
            { title: "Bucket", dataIndex: "bucket", width: 160, render: (v) => <span className="osi-num">{dayjs(v).format("D MMM YYYY")}</span> },
            { title: "Hours", dataIndex: "totalMinutes", width: 100, align: "right", render: (v) => <span className="osi-num">{hrs(v)}</span> },
            {
              title: "",
              // inline bar: the distribution is the point of a timeline, and it
              // costs no new data to draw
              render: (_, r: any) => (
                <div style={{ height: 8, background: "var(--osi-brand-700)", width: `${(r.totalMinutes / peak) * 100}%`, minWidth: 2 }} />
              ),
            },
          ]}
        />
      </DisplayComponent>
      )}
    </>
  );
};

const VelocityTab = () => {
  const { data: projects } = useProjects();
  const [projectId, setProjectId] = useState<string>();
  const { data: velocity, isLoading, isError, error } = useReportVelocity(projectId);
  const peak = Math.max(...(velocity || []).map((v: any) => v.completedPoints), 1);

  return (
    <>
      <Filters onClear={() => setProjectId(undefined)}>
        <Select
          allowClear
          style={{ width: 240 }} placeholder="All projects"
          value={projectId}
          options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          onChange={setProjectId}
        />
      </Filters>
      {(
        <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
          <Table
            rowKey="sprintId" size="small" dataSource={velocity || []} pagination={false}
            locale={{ emptyText: <EmptyState title="No completed sprints yet" description="Velocity appears once a sprint closes with points completed." /> }}
            columns={[
              { title: "Sprint", dataIndex: "sprintName", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
              { title: "Points", dataIndex: "completedPoints", width: 90, align: "right", render: (v) => <span className="osi-num">{v}</span> },
              {
                title: "",
                render: (_, r: any) => (
                  <div style={{ height: 8, background: "var(--osi-brand-700)", width: `${(r.completedPoints / peak) * 100}%`, minWidth: 2 }} />
                ),
              },
            ]}
          />
        </DisplayComponent>
      )}
    </>
  );
};

export const ReportsPage = () => {
  const [tab, setTab] = useState("download");
  return (
    <PageShell
      kind="list"
      kicker="Analytics"
      title="Reports"
      subtitle="Download support-team reports, and review logged time across every project."
      tabs={
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "download", label: "Download" },
            { key: "by-project", label: "Logged time by project" },
            { key: "by-user", label: "Logged time by person" },
            { key: "timeline", label: "Timeline" },
            { key: "velocity", label: "Velocity" },
          ]}
        />
      }
    >
      {tab === "download" && <DownloadTab />}
      {tab === "by-project" && <ByProjectTab />}
      {tab === "by-user" && <ByUserTab />}
      {tab === "timeline" && <TimelineTab />}
      {tab === "velocity" && <VelocityTab />}
    </PageShell>
  );
};
